/** Formulaire produit. */
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { RichText, richOrPlain } from "../../components/RichText";
import { useFormatters } from "../../formats/useFormatters";
import "../clients/clientShow.css";
import "./catalog.css";
import { PcgFields } from "../../pcg/PcgFields";
import { PCG_SALES } from "../../pcg/pcg";
import { lineTaxCents, taxOf } from "./format";
import { inputToCents, typedDecimal, type ProductDraft, type TaxRate } from "./types";

type CatalogFormProps = {
  draft: ProductDraft;
  setDraft: (next: ProductDraft) => void;
  rates: TaxRate[];
  decimalComma: boolean;
  busy: boolean;
  error: string;
  onSave: () => void;
  onCancel: () => void;
  onDeactivate?: () => void;
  onPurge?: () => void;
};

export function CatalogForm({
  draft,
  setDraft,
  rates,
  decimalComma,
  busy,
  error,
  onSave,
  onCancel,
  onDeactivate,
  onPurge,
}: CatalogFormProps) {
  const { money, formats } = useFormatters();
  const cents = inputToCents(draft.price, decimalComma);
  const costCents = inputToCents(draft.cost, decimalComma);
  const tax = taxOf(rates, draft.tax_rate_id);
  const tva = cents !== null && tax ? lineTaxCents(cents, tax.rate_bp) : null;
  const symbol = formats.currency_display === "code" ? "EUR" : "€";

  function set<K extends keyof ProductDraft>(key: K, value: ProductDraft[K]) {
    setDraft({ ...draft, [key]: value });
  }

  return (
    <form
      className="fx-catalog-form mb-4 flex w-full flex-col gap-5 rounded-2xl bg-white p-5 ring-1 ring-indigo-100"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <div>
        <button type="button" className="fx-fiche-back" onClick={onCancel}>
          ← Catalogue
        </button>
        <h1 className="fx-title m-0 font-[Nunito] text-2xl text-indigo-950">
          {draft.id ? "Modifier le produit" : "Nouveau produit"}
        </h1>
      </div>
      {error ? <p className="m-0 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">{error}</p> : null}
      <div className="grid w-full gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field
            label="Libellé"
            name="label"
            value={draft.label}
            required
            autoComplete="off"
            onChange={(e) => set("label", e.target.value)}
          />
        </div>
        <div className="sm:col-span-2">
          <RichText
            label="Descriptif"
            fonts={false}
            resetKey={draft.id ?? "new"}
            value={richOrPlain(draft.description)}
            onChange={(html) => set("description", html)}
          />
        </div>
        <Field
          label="Code produit"
          name="product_code"
          value={draft.product_code}
          placeholder="70610000"
          autoComplete="off"
          onChange={(e) => set("product_code", e.target.value)}
        />
        <Field
          label="Unité"
          name="unit"
          value={draft.unit}
          placeholder="u"
          hint="u, h, j, forfait…"
          autoComplete="off"
          onChange={(e) => set("unit", e.target.value)}
        />
        <Field
          label="Prix HT"
          name="price"
          inputMode="decimal"
          value={draft.price}
          placeholder={decimalComma ? "0,00" : "0.00"}
          suffix={<span className="text-xs" style={{ color: "var(--fx-muted)" }}>{symbol}</span>}
          onChange={(e) => set("price", typedDecimal(e.target.value, decimalComma))}
        />
        <Field
          label="Coût HT"
          name="cost"
          inputMode="decimal"
          value={draft.cost}
          placeholder={decimalComma ? "0,00" : "0.00"}
          hint="Interne — jamais sur le PDF."
          suffix={<span className="text-xs" style={{ color: "var(--fx-muted)" }}>{symbol}</span>}
          onChange={(e) => set("cost", typedDecimal(e.target.value, decimalComma))}
        />
        <Field
          label="Quantité par défaut"
          name="default_qty"
          type="number"
          min={1}
          max={9999}
          value={String(draft.default_qty)}
          onChange={(e) => set("default_qty", Math.min(9999, Math.max(1, Number(e.target.value) || 1)))}
        />
        <div className="sm:col-span-2">
          <p className="mb-1 text-xs" style={{ color: "var(--fx-muted)" }}>
            Nature des opérations
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="op_kind"
                checked={draft.op_kind === "good"}
                onChange={() => set("op_kind", "good")}
              />
              Biens
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="op_kind"
                checked={draft.op_kind === "service"}
                onChange={() => set("op_kind", "service")}
              />
              Services
            </label>
          </div>
          <p className="fx-co-form__hint m-0 mt-1">
            La facture en déduit BIENS, SERVICES ou MIXTE.
          </p>
        </div>
        <label className="fx-field flex flex-col gap-1 text-xs" htmlFor="tax_rate_id">
          TVA
          <select
            id="tax_rate_id"
            className="rounded-md border px-2.5 py-2 text-sm"
            value={draft.tax_rate_id ?? ""}
            onChange={(e) => set("tax_rate_id", e.target.value === "" ? null : Number(e.target.value))}
          >
            <option value="">—</option>
            {rates.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
                {r.is_default ? " (défaut)" : ""}
              </option>
            ))}
          </select>
        </label>
        <div className="sm:col-span-2">
          <PcgFields
            accounts={PCG_SALES}
            code={draft.pcg_code}
            sub={draft.pcg_sub}
            onCode={(v) => set("pcg_code", v)}
            onSub={(v) => set("pcg_sub", v)}
          />
        </div>
      </div>
      {cents !== null && tax ? (
        <p className="m-0 text-sm" style={{ color: "var(--fx-muted)" }}>
          TVA {money(tva ?? 0)} · TTC {money(cents + (tva ?? 0))}
          {costCents !== null && costCents > 0 ? ` · marge ${money(cents - costCents)}` : ""}
        </p>
      ) : cents === null || costCents === null ? (
        <p className="m-0 text-sm text-red-700">Prix ou coût invalide.</p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2">
        {onDeactivate && draft.active ? (
          <Button variant="danger" onClick={onDeactivate} disabled={busy}>
            Désactiver
          </Button>
        ) : null}
        {onPurge && draft.id && !draft.active ? (
          <Button variant="danger" onClick={onPurge} disabled={busy}>
            Supprimer définitivement
          </Button>
        ) : null}
        <Button variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" disabled={busy}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}
